import { Dialog, DialogContent, DialogTitle, List, ListItem } from '@mui/material';
import { AgBarSeriesItemStylerParams, AgBarSeriesStyle, AgSparklineOptions } from 'ag-charts-community';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { isSameDay } from 'date-fns';
import Holidays from 'date-holidays';
import dayjs from 'dayjs';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, permissionLevelEnum } from '../../../../constants';
import { useMonthlySubContractorsQuery } from '../../../../hooks/queries/useSubContractorsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useTheme } from '../../../../hooks/useTheme';
import { MonthlySubContractor } from '../../../../services/apiSLTP/materials/subContractors';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { getColorByPercentage } from '../../../../utils/colors';
import StateCell from '../../../cells/StateCell';
import IconButton from '../../../ui/IconButton';
import GenericAgGridTable from '../../GenericAgGridTable';
const hd = new Holidays('FR');

interface MonthlySubContractorTypesTableProps {
    currentDate: Date;
}

const MonthlySubContractorTypesTable: React.FC<MonthlySubContractorTypesTableProps> = ({ currentDate }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { muiTheme, mode } = useTheme();
    const [selectedSubContractorType, setSelectedSubContractorType] = useState<
        MonthlySubContractor['dailySubContractorsByDate'][number]['daily'] | null
    >(null);

    const { data: dataMonthlySubContractorTypes, isLoading: isMonthlySubContractorTypesLoading } = useMonthlySubContractorsQuery({
        params: { date: currentDate.toISOString() },
    });

    const daysInMonth = useMemo(() => {
        const days = [];
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const numDays = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= numDays; day++) {
            const d = new Date(year, month, day);
            days.push({
                date: d,
                label: d.getDate().toString(),
            });
        }
        return days;
    }, [currentDate]);

    const rowData = useMemo(() => {
        if (!dataMonthlySubContractorTypes) return [];

        return dataMonthlySubContractorTypes.sort((a, b) => a.designation.localeCompare(b.designation));
    }, [dataMonthlySubContractorTypes, daysInMonth]);

    const itemStyler: (params: AgBarSeriesItemStylerParams<any>) => AgBarSeriesStyle = (params) => {
        const max = Math.max(...rowData.map((value) => value.dailySubContractorsByDate.length));

        const color = getColorByPercentage(params.yValue ?? 0, max);
        return { fill: color };
    };

    const columnDefs: ColDef[] = useMemo(() => {
        const baseCols: ColDef<MonthlySubContractor>[] = [
            {
                headerName: capitalizeFirstLetter(t('name')),
                field: 'designation',
                colId: 'name',
                pinned: 'left',
                initialWidth: 300,
                cellStyle: (params) =>
                    params.data?.crudStatus === crudStatusEnum.DELETED
                        ? { color: muiTheme.palette.error.main }
                        : params.data?.crudStatus === crudStatusEnum.INACTIVE
                          ? { color: muiTheme.palette.action.disabled }
                          : undefined,
            },
            {
                headerName: capitalizeFirstLetter(t('codeProC')),
                field: 'codeProC',
                colId: 'codeProC',
                pinned: 'left',
                initialWidth: 120,
            },

            {
                headerName: capitalizeFirstLetter(t('prodDays')),
                colId: 'totalparkline',
                cellRenderer: 'agSparklineCellRenderer',
                pinned: 'left',
                valueGetter: (params) => {
                    return [params.data?.dailySubContractorsByDate.length];
                },
                comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
                    return valueA - valueB;
                },
                cellRendererParams: {
                    sparklineOptions: {
                        type: 'bar',
                        direction: 'horizontal',
                        min: 0,
                        max: Math.max(...rowData.map((value) => value.dailySubContractorsByDate.length)) + 5,

                        label: {
                            enabled: true,
                            placement: 'outside-end',
                            color: mode === 'dark' ? '#fff' : '#000',
                        },

                        itemStyler: itemStyler,
                    } as AgSparklineOptions,
                },
            },
        ];

        const dateCols: ColDef<MonthlySubContractor>[] = daysInMonth.map(({ date, label }) => ({
            headerName: label,
            colId: label,
            initialWidth: 120,
            valueGetter: (params) => params.data?.dailySubContractorsByDate.find((value) => isSameDay(new Date(value.date), date))?.daily.length,
            cellRenderer: (params: ICellRendererParams<MonthlySubContractor>) => {
                const currentSubContractorTypeDay = params.data?.dailySubContractorsByDate.find((value) => isSameDay(new Date(value.date), date));
                return currentSubContractorTypeDay ? (
                    <div className="fx-r fx-items-c gap-5px text-grey">
                        <StateCell text={currentSubContractorTypeDay.daily.length.toString()} stateColor={''} showDot={false} />
                        {!!currentSubContractorTypeDay.daily.length && (
                            <IconButton iconName="infoSharp" onClick={() => setSelectedSubContractorType(currentSubContractorTypeDay.daily)} />
                        )}
                    </div>
                ) : (
                    <div className=" text-gray">{t('n/a').toUpperCase()}</div>
                );
            },
            headerStyle: {
                color: hd.isHoliday(date)
                    ? muiTheme.palette.error.main
                    : date.getDay() === 6
                      ? muiTheme.palette.warning.main
                      : date.getDay() === 0
                        ? muiTheme.palette.error.main
                        : '',
            },
            suppressFloatingFilterButton: true,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
        }));

        const adminCols: ColDef<MonthlySubContractor>[] =
            user?.profileId.permsLevel === permissionLevelEnum.ADMIN
                ? [
                      {
                          headerName: '_id',
                          field: '_id',
                          colId: '_id',
                          initialWidth: 250,
                          cellClass: 'usage-col-main',
                          headerClass: 'usage-col-main',
                      },
                  ]
                : [];

        return [...baseCols, ...dateCols, ...adminCols];
    }, [t, daysInMonth, user, rowData, mode]);

    return (
        <>
            <GenericAgGridTable
                rowData={rowData}
                columnDefs={columnDefs}
                exportCsvFileName={`presence-subContractors-${dayjs(currentDate).format('YYYY-MM')}`}
                agGridProps={{ getRowId: ({ data }) => data._id }}
                loading={isMonthlySubContractorTypesLoading}
            />
            <Dialog open={!!selectedSubContractorType} onClose={() => setSelectedSubContractorType(null)}>
                <DialogTitle>{capitalizeFirstLetter(t('sites'))}</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedSubContractorType?.length ? (
                            selectedSubContractorType?.map((daily, i) => (
                                <ListItem key={i} className="gap-5px fx-r">
                                    <span className="font-w-700">{daily?.site.name}</span>
                                </ListItem>
                            ))
                        ) : (
                            <ListItem>{t('noSite')}</ListItem>
                        )}
                    </List>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default MonthlySubContractorTypesTable;
